<template>    
   <div class="d-flex admin-container">
    <Sidebar />

    <div class="main-content mt-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2 class="section-title">📋 Listado de Prospectos</h2>

        <button class="btn btn-success d-flex align-items-center" @click="abrirNuevoProspecto">
          <i class="fas fa-plus-circle mr-2"></i> Agregar Prospecto
        </button>
      </div>

      <!-- Filtro por estado -->
      <div class="filter-card p-3 mb-4 d-flex align-items-end gap-3 flex-wrap">
        <div class="form-group mb-0 w-100 w-md-25">
          <label for="estadoFiltro" class="mb-1 font-weight-bold">Estado</label>
          <select v-model="estadoFiltro" class="form-control" id="estadoFiltro">
            <option value="">Todos</option>
            <option>Nuevo</option>
            <option>Contactado</option>
            <option>En proceso</option>
            <option>Contratado</option>
            <option>No interesado</option>
          </select>
        </div>
      </div>

      <div class="kanban-board d-flex gap-3 overflow-auto animate__animated animate__fadeIn">

        <!-- Recorremos todos los estados -->
        <div
          class="kanban-column"
          v-for="estado in estados"
          :key="estado"
          v-show="!estadoFiltro || estadoFiltro === estado" 
        >
          <h5 class="kanban-title">{{ estado }}</h5>

          <!-- Si NO hay filtro mostramos una sola columna vertical -->
          <div 
            v-if="!estadoFiltro"
            class="kanban-card-group-single-column"
          >
            <div
              v-for="prospecto in prospectosPorEstado(estado)"
              :key="prospecto.id"
              class="kanban-card"
            >
              <!-- Carta prospecto (igual que antes) -->
              <div class="name-badge">
                <strong class="prospecto-nombre" :title="prospecto.nombre">{{ prospecto.nombre }}</strong>
              </div>
              <div>
                <span :class="['badge', estadoColor(prospecto.estado)]" class="estado-badge">{{ prospecto.estado }}</span>
              </div>
              <div class="info-line">
                <i class="fas fa-phone-alt icon-inline"></i>
                <span class="info-text">{{ prospecto.telefono }}</span>
              </div>
              <div class="info-line correo-line">
                <i class="fas fa-envelope icon-inline"></i>
                <span class="info-text correo-text">{{ prospecto.correo }}</span>
              </div>
              <div class="info-line">
                <i class="fas fa-calendar-alt icon-inline"></i>
                <span class="info-text">{{ formatFecha(prospecto.fecha_registro) }}</span>
              </div>
              <p class="mensaje-text">
                <i class="fas fa-file-alt icon-inline"></i>
                <span v-if="mensajeExpandidoId === prospecto.id">{{ prospecto.mensaje }}</span>
                <span v-else>{{ prospecto.mensaje?.length > 30 ? prospecto.mensaje.slice(0, 30) + '...' : prospecto.mensaje || '—' }}</span>
                <button
                  v-if="prospecto.mensaje?.length > 30"
                  class="btn-link ml-1"
                  @click="toggleMensaje(prospecto.id)"
                  type="button"
                >
                  {{ mensajeExpandidoId === prospecto.id ? 'Ver menos' : 'Ver más' }}
                </button>
              </p>
              <div class="d-flex justify-content-end mt-3 icon-actions">
                <font-awesome-icon icon="edit" class="text-primary mx-1 cursor-pointer icon-uniform" @click="abrirEdicion(prospecto)" title="Editar" />
                <font-awesome-icon icon="trash-alt" class="text-danger mx-1 cursor-pointer icon-uniform" @click="confirmarEliminacion(prospecto.id)" title="Eliminar" />
                <a :href="`https://wa.me/52${prospecto.telefono}?text=Hola ${prospecto.nombre}, te contactamos desde GmRisk.`" target="_blank" class="whatsapp-link">
                  <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-success mx-1 icon-uniform" title="WhatsApp" />
                </a>
                <button class="btn btn-link p-0 ml-2" @click="agendarCita(prospecto)" title="Agendar Cita">
                  <i class="fas fa-calendar-check text-info icon-uniform"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Si HAY filtro mostramos subcolumnas, cada una con hasta 10 prospectos -->
          <div v-else class="kanban-card-group">
            <div
              class="kanban-card-subcolumn"
              v-for="(grupo, index) in groupedProspectosPorEstado(estado)"
              :key="index"
            >
              <div
                class="kanban-card kanban-card-filtrado"
                v-for="prospecto in grupo"
                :key="prospecto.id"
              >
                <!-- Carta prospecto filtrado (igual que antes) -->
                <div class="name-badge">
                  <strong class="prospecto-nombre" :title="prospecto.nombre">{{ prospecto.nombre }}</strong>
                </div>
                <div>
                  <span :class="['badge', estadoColor(prospecto.estado)]" class="estado-badge">{{ prospecto.estado }}</span>
                </div>
                <div class="info-line">
                  <i class="fas fa-phone-alt icon-inline"></i>
                  <span class="info-text">{{ prospecto.telefono }}</span>
                </div>
                <div class="info-line correo-line">
                  <i class="fas fa-envelope icon-inline"></i>
                  <span class="info-text correo-text">{{ prospecto.correo }}</span>
                </div>
                <div class="info-line">
                  <i class="fas fa-calendar-alt icon-inline"></i>
                  <span class="info-text">{{ formatFecha(prospecto.fecha_registro) }}</span>
                </div>
                <p class="mensaje-text">
                  <i class="fas fa-file-alt icon-inline"></i>
                  <span v-if="mensajeExpandidoId === prospecto.id">{{ prospecto.mensaje }}</span>
                  <span v-else>{{ prospecto.mensaje?.length > 30 ? prospecto.mensaje.slice(0, 30) + '...' : prospecto.mensaje || '—' }}</span>
                  <button
                    v-if="prospecto.mensaje?.length > 30"
                    class="btn-link ml-1"
                    @click="toggleMensaje(prospecto.id)"
                    type="button"
                  >
                    {{ mensajeExpandidoId === prospecto.id ? 'Ver menos' : 'Ver más' }}
                  </button>
                </p>
                <div class="d-flex justify-content-end mt-3 icon-actions">
                  <font-awesome-icon icon="edit" class="text-primary mx-1 cursor-pointer icon-uniform" @click="abrirEdicion(prospecto)" title="Editar" />
                  <font-awesome-icon icon="trash-alt" class="text-danger mx-1 cursor-pointer icon-uniform" @click="confirmarEliminacion(prospecto.id)" title="Eliminar" />
                  <a :href="`https://wa.me/52${prospecto.telefono}?text=Hola ${prospecto.nombre}, te contactamos desde GmRisk.`" target="_blank" class="whatsapp-link">
                    <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-success mx-1 icon-uniform" title="WhatsApp" />
                  </a>
                  <button class="btn btn-link p-0 ml-2" @click="agendarCita(prospecto)" title="Agendar Cita">
                    <i class="fas fa-calendar-check text-info icon-uniform"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
      <!-- Modal de edición y creación -->
      <div v-if="mostrarModal" class="modal-backdrop">
        <div class="modal-container">
          <h5 class="mb-3">{{ creandoNuevo ? '➕ Agregar Prospecto' : '✏️ Editar Prospecto' }}</h5>
          <form @submit.prevent="creandoNuevo ? crearProspecto() : guardarCambios()">
            <div class="form-group">
              <label>Nombre</label>
              <input v-model="prospectoEditando.nombre" class="form-control" required />
            </div>
            <div class="form-group">
              <label>Correo</label>
              <input v-model="prospectoEditando.correo" class="form-control" required />
            </div>
            <div class="form-group">
              <label>Teléfono</label>
              <input v-model="prospectoEditando.telefono" class="form-control" required />
            </div>

            <div v-if="creandoNuevo" class="form-group">
              <label>Tipo de Seguro</label>
              <select v-model="prospectoEditando.tipo_seguro" class="form-control" required>
                <option disabled value="">Selecciona un seguro</option>
                <option>Seguro de Gastos Médicos Mayores</option>
                <option>Seguro de vida</option>
                <option>Seguro Educativo</option>
                <option>Seguro de Ahorro</option>
                <option>Seguro para la Pensión</option>
                <option>Seguros para la mujer</option>
                <option>Seguro de Viajero</option>
                <option>Seguro Hombre clave</option>
                <option>Seguro Inter Socios</option>
                <option>Seguro de Auto</option>
                <option>Seguro de Casa</option>
                <option>Seguro de Embarcaciones</option>
                <option>Seguro Vida Grupo</option>
                <option>Seguro Colectivo Gastos Médicos</option>
                <option>Seguro de Flotillas</option>
                <option>Seguro de Transporte</option>
                <option>Seguros Empresariales</option>
              </select>
            </div>

            <div class="form-group">
              <label>Estado</label>
              <select v-model="prospectoEditando.estado" class="form-control">
                <option>Nuevo</option>
                <option>Contactado</option>
                <option>En proceso</option>
                <option>Contratado</option>
                <option>No interesado</option>
              </select>
            </div>
            <div class="text-right mt-3">
              <button class="btn btn-secondary mr-2" @click="cerrarModal" type="button">Cancelar</button>
              <button type="submit" class="btn btn-primary">{{ creandoNuevo ? 'Agregar' : 'Guardar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue';
import Swal from 'sweetalert2';

export default {
  name: 'Prospectos',
  components: { Sidebar },
  data() {
    return {
      prospectos: [],
      mostrarModal: false,
      prospectoEditando: null,
      creandoNuevo: false,
      estadoFiltro: '',
      mensajeExpandidoId: null,
      estados: ['Nuevo', 'Contactado', 'En proceso', 'Contratado', 'No interesado'],
    };
  },
  computed: {
    prospectosFiltrados() {
      if (!this.estadoFiltro) return this.prospectos;
      return this.prospectos.filter((p) => (p.estado || 'Nuevo') === this.estadoFiltro);
    },
  },
  methods: {
    async fetchProspectos() {
      try {
        const response = await fetch('http://localhost:3000/api/prospectos');
        const data = await response.json();
        this.prospectos = data;
      } catch (error) {
        console.error('Error al cargar prospectos:', error);
      }
    },
    abrirEdicion(prospecto) {
      this.creandoNuevo = false;
      this.prospectoEditando = { ...prospecto };
      this.mostrarModal = true;
    },
    abrirNuevoProspecto() {
      this.creandoNuevo = true;
      this.prospectoEditando = {
        nombre: '',
        correo: '',
        telefono: '',
        tipo_seguro: '',
        estado: 'Nuevo',
      };
      this.mostrarModal = true;
    },
    cerrarModal() {
      this.mostrarModal = false;
      this.prospectoEditando = null;
    },
    async guardarCambios() {
  try {
    const response = await fetch(`http://localhost:3000/api/prospectos/${this.prospectoEditando.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.prospectoEditando),
    });
    if (response.ok) {
      await this.fetchProspectos();
      this.cerrarModal();
      Swal.fire({
        icon: 'success',
        title: 'Prospecto editado correctamente',
        showConfirmButton: false,
        timer: 2000,
      });
    } else {
      throw new Error('Error en la respuesta del servidor');
    }
  } catch (error) {
    console.error('Error al actualizar prospecto:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error al editar prospecto',
      text: 'Ocurrió un problema al guardar los cambios.',
    });
  }
},

async crearProspecto() {
  try {
    const response = await fetch(`http://localhost:3000/api/prospectos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.prospectoEditando),
    });
    if (response.ok) {
      await this.fetchProspectos();
      this.cerrarModal();
      Swal.fire({
        icon: 'success',
        title: 'Prospecto agregado correctamente',
        showConfirmButton: false,
        timer: 2000,
      });
    } else {
      throw new Error('Error en la respuesta del servidor');
    }
  } catch (error) {
    console.error('Error al crear prospecto:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error al agregar prospecto',
      text: 'Ocurrió un problema al crear el prospecto.',
    });
  }
},

    confirmarEliminacion(id) {
      Swal.fire({
        title: '¿Eliminar prospecto?',
        text: 'Esta acción no se puede deshacer',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await fetch(`http://localhost:3000/api/prospectos/${id}`, {
              method: 'DELETE',
            });
            if (response.ok) {
              this.prospectos = this.prospectos.filter((p) => p.id !== id);
              Swal.fire('Eliminado', 'El prospecto fue eliminado.', 'success');
            }
          } catch (error) {
            console.error('Error al eliminar:', error);
            Swal.fire('Error', 'No se pudo eliminar el prospecto.', 'error');
          }
        }
      });
    },
    toggleMensaje(id) {
      this.mensajeExpandidoId = this.mensajeExpandidoId === id ? null : id;
    },
    estadoColor(estado) {
      switch ((estado || 'Nuevo').toLowerCase()) {
        case 'nuevo':
          return 'badge-nuevo';
        case 'contactado':
          return 'badge-contactado';
        case 'en proceso':
          return 'badge-en-proceso';
        case 'contratado':
          return 'badge-contratado';
        case 'no interesado':
          return 'badge-no-interesado';
        default:
          return 'badge-secondary';
      }
    },
    prospectosPorEstado(estado) {
      // Si hay filtro usamos sólo prospectos filtrados, si no todos
      const fuente = this.estadoFiltro ? this.prospectosFiltrados : this.prospectos;
      return fuente.filter((p) => (p.estado || 'Nuevo') === estado);
    },
    groupedProspectosPorEstado(estado) {
      // Divide la lista en grupos de 10 para mostrar subcolumnas
      const lista = this.prospectosPorEstado(estado);
      const columnas = [];
      for (let i = 0; i < lista.length; i += 10) {
        columnas.push(lista.slice(i, i + 10));
      }
      return columnas;
    },
    formatFecha(fechaISO) {
      if (!fechaISO) return '—';
      const fecha = new Date(fechaISO);
      if (isNaN(fecha)) return '—';
      const dia = fecha.getDate().toString().padStart(2, '0');
      const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
      const anio = fecha.getFullYear();
      return `${dia}/${mes}/${anio}`;
    },
    agendarCita(prospecto) {
      Swal.fire({
        title: 'Agendar Cita',
        text: `Agendar cita para ${prospecto.nombre}`,
        icon: 'info',
        confirmButtonText: 'OK',
      });
    },
  },
  mounted() {
    this.fetchProspectos();
  },
};
</script>

<style scoped>
@import 'https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css';

.admin-container {
  width: 100%;
  min-height: 100vh;
}

.main-content {
  margin-left: 250px;
  width: calc(100% - 250px);
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.filter-card {
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.badge {
  padding: 0.4em 0.7em;
  border-radius: 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
}

.badge-nuevo {
  background-color: #007BFF;
  color: white;
}

.badge-contactado {
  background-color: #17a2b8;
  color: white;
}

.badge-en-proceso {
  background-color: #6f42c1;
  color: white;
}

.badge-contratado {
  background-color: #28a745;
  color: white;
}

.badge-no-interesado {
  background-color: #dc3545;
  color: white;
}




.kanban-title {
  font-weight: 700;
  font-size: 1.25rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
}




.kanban-card-filtrado {
  min-width: 280px;
  max-width: 300px;
}

.name-badge {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.prospecto-nombre {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  overflow-wrap: anywhere;
}

.estado-badge {
  font-size: 0.8rem;
  padding: 0.3em 0.5em;
}

.info-line {
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
}

.icon-inline {
  margin-right: 0.5rem;
  color: #6c757d;
  width: 18px;
  text-align: center;
}

.info-text {
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap; /* Evita que el texto se divida en varias líneas */
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
}

.correo-line .info-text {
  white-space: nowrap; /* clave para que el correo no se divida */
}

.mensaje-text {
  margin-top: 0.8rem;
  font-size: 0.9rem;
  color: #444;
  word-break: break-word;
}

.btn-link {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 0;
  font-size: 0.9rem;
}

.icon-actions {
  font-size: 1.1rem;
}

.icon-uniform {
  font-size: 1.2rem;
  cursor: pointer;
}

.whatsapp-link {
  text-decoration: none;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-container {
  background: white;
  border-radius: 10px;
  padding: 2rem;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
}

.form-group {
  margin-bottom: 1rem;
}

.form-control {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  color: #495057;
}

.text-right {
  text-align: right;
}

.mt-3 {
  margin-top: 1rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.kanban-board {
  width: 100%;
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 1rem;
}

.kanban-column {
  background: #f1f1f1;
  border-radius: 8px;
  padding: 1rem;
  flex: 1;
  min-width: 300px;
}

.kanban-card {
  background: white;
  border: 1px solid #dee2e6;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.kanban-card-group {
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
}

.kanban-card-subcolumn {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: unset;
  min-width: 300px;
}


</style>
